import { useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Container } from "react-bootstrap";
import remarkGfm from "remark-gfm";
import authorAvatar from "../../assets/images/icons/app-icon.png";
import "./blog.css";

const DEFAULT_AUTHOR = {
  name: "Tanaka Misaki",
  role: "Language Learning Experts",
  avatar: authorAvatar,
};
const DEFAULT_SITE_FILTER = "&filters[$or][0][site][$null]=true&filters[$or][1][site][$eq]=&filters[$or][2][site][$eq]=ikiai.org";

function estimateReadingTime(content) {
  if (!content) return 0;
  const words = content.split(/\s+/).filter((w) => w.length > 0).length;
  return Math.max(1, Math.ceil(words / 220));
}

export default function BlogDetail() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [blog, setBlog] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;

    const fetchBlog = async () => {
      setIsLoading(true);
      const reqUrl = `${process.env.REACT_APP_STRAPI_URL}/api/posts?locale=${i18n.language}${DEFAULT_SITE_FILTER}&filters[slug][$eq]=${encodeURIComponent(slug)}&fields=title,updatedAt,content&populate=coverImage`;
      try {
        const res = await fetch(reqUrl);
        const json = await res.json();
        if (json.data && json.data.length > 0) {
          setBlog(json.data[0]);
        } else {
          setBlog(null);
        }
      } catch (err) {
        console.error("Blog detail fetch failed:", err);
        setBlog(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBlog();
  }, [slug, i18n.language]);

  // Fetch related posts (other posts excluding current)
  useEffect(() => {
    if (!slug) return;

    const fetchRelated = async () => {
      const reqUrl = `${process.env.REACT_APP_STRAPI_URL}/api/posts?locale=${i18n.language}${DEFAULT_SITE_FILTER}&filters[slug][$ne]=${encodeURIComponent(slug)}&fields=title,slug,updatedAt&populate=coverImage&sort=updatedAt:desc&pagination[pageSize]=4`;
      try {
        const res = await fetch(reqUrl);
        const json = await res.json();
        if (json.data) {
          setRelatedPosts(json.data);
        }
      } catch {
        // silently ignore
      }
    };

    fetchRelated();
  }, [slug, i18n.language]);

  if (isLoading) {
    return (
      <Container className="blog-container">
        <div className="blog-loading-container">
          <div className="blog-loading-spinner"></div>
        </div>
      </Container>
    );
  }

  if (!blog) {
    return (
      <Container className="blog-container">
        <div className="maxw">
          <Link to="/blog" className="blog-back-link global-text">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            {t("blog.back_to_blog") || "Back to Blog"}
          </Link>
          <p className="global-text">{t("blog.article_not_found")}</p>
        </div>
      </Container>
    );
  }

  const coverImage = blog.coverImage;
  const coverUrl = coverImage
    ? (coverImage.formats?.large?.url || coverImage.url)
    : null;
  const fullCoverUrl = coverUrl
    ? (coverUrl.startsWith("http") ? coverUrl : `${process.env.REACT_APP_STRAPI_URL}${coverUrl}`)
    : null;

  const readingTime = estimateReadingTime(blog.content);

  const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString(i18n.language, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getRelatedCoverUrl = (post) => {
    const cover = post.coverImage;
    if (!cover) return null;
    const url = cover.formats?.medium?.url || cover.formats?.small?.url || cover.url;
    if (!url) return null;
    return url.startsWith("http") ? url : `${process.env.REACT_APP_STRAPI_URL}${url}`;
  };

  return (
    <Container className="blog-container blog-detail">
      <div className="maxw">
        <Link to="/blog" className="blog-back-link global-text">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          {t("blog.back_to_blog") || "Back to Blog"}
        </Link>

        {fullCoverUrl && (
          <div className="blog-hero-image">
            <img src={fullCoverUrl} alt={blog.title} />
          </div>
        )}

        <div className="blog-article-header">
          <h1 className="global-title">{blog.title}</h1>

          <div className="blog-author">
            <img
              className="blog-author-avatar"
              src={DEFAULT_AUTHOR.avatar}
              alt={DEFAULT_AUTHOR.name}
              width="40"
              height="40"
            />
            <div className="blog-author-info">
              <span className="blog-author-name global-subtitle">{DEFAULT_AUTHOR.name}</span>
              <span className="blog-author-role global-text">{DEFAULT_AUTHOR.role}</span>
            </div>
          </div>

          <div className="blog-article-meta global-text">
            <span>{formatDate(blog.updatedAt)}</span>
            <span className="divider" />
            <span>{readingTime} mins read</span>
          </div>
        </div>

        <div className="markdown-content global-text">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              img: ({ src, alt, ...props }) => {
                const fixedSrc = src ? src.replace(/([^:])\/\//g, "$1/") : src;
                return <img src={fixedSrc} alt={alt} loading="lazy" {...props} />;
              },
            }}
          >
            {blog.content}
          </ReactMarkdown>
        </div>

        {/* Related Articles */}
        {relatedPosts.length > 0 && (
          <div className="blog-related">
            <div className="blog-related-title global-subtitle">
              {t("blog.related_articles") || "More Articles"}
            </div>
            <div className="blog-related-grid">
              {relatedPosts.map((post) => {
                const relCoverUrl = getRelatedCoverUrl(post);
                return (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="blog-related-card"
                  >
                    <div className="blog-related-card-image">
                      {relCoverUrl ? (
                        <img src={relCoverUrl} alt={post.title} loading="lazy" />
                      ) : null}
                    </div>
                    <div className="blog-related-card-body">
                      <div className="blog-related-card-date global-text">
                        {formatDate(post.updatedAt)}
                      </div>
                      <div className="blog-related-card-title global-subtitle">
                        {post.title}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Container>
  );
}
